import {
	type SabClientListItemDto,
	SabClientType,
} from "Infrastructure/Api/generated/types.gen";

export const SELECTED_CLIENT_KEY = "selectedClient";
export const SELECTED_CLIENT_EPOCHS_KEY = "selectedClient.epochs";
export const CLIENT_EPOCH_CHANGED_EVENT = "clientEpoch:changed";

export type StoredSelectedClient = {
	sabClientID: number;
	sabIdentificationNumber: string;
	displayName: string;
	/**
	 * Physical person vs. company. Decides which identification-number field
	 * {@link import("Modeling/Services/storedClientPickerMapping").mapStoredToPickerCandidate}
	 * fills when reconstructing a picker candidate — getting it wrong sends a
	 * personal id where a company id is expected (or vice versa) on conversion.
	 * Optional for backward compatibility with values stored before this field
	 * existed; defaults to PhysicalPerson on read.
	 */
	clientType: SabClientType;
};

/**
 * Per-sabClientID counter bumped every time the user leaves a client context
 * (selectedClient transitions away from `client.sabClientID`). The
 * versioning workspace reads this when sending mutations: a mismatch with the
 * run's last-edited epoch on the server triggers a fork into a new version.
 *
 * The map is cleared on sign-out so that re-signing-in doesn't look like a
 * boundary crossing for an unrelated user.
 */
type ClientEpochMap = Record<number, number>;

export const readStoredSelectedClient = (): StoredSelectedClient | null => {
	const raw = localStorage.getItem(SELECTED_CLIENT_KEY);
	if (raw === null) return null;
	try {
		const parsed = JSON.parse(raw) as Partial<StoredSelectedClient>;
		if (
			typeof parsed.sabClientID !== "number" ||
			typeof parsed.sabIdentificationNumber !== "string" ||
			typeof parsed.displayName !== "string"
		) {
			return null;
		}
		return {
			sabClientID: parsed.sabClientID,
			sabIdentificationNumber: parsed.sabIdentificationNumber,
			displayName: parsed.displayName,
			// Default to PhysicalPerson for values stored before clientType existed.
			clientType:
				parsed.clientType === SabClientType.Company
					? SabClientType.Company
					: SabClientType.PhysicalPerson,
		};
	} catch {
		return null;
	}
};

export const writeStoredSelectedClient = (
	value: StoredSelectedClient | null,
): void => {
	try {
		if (value === null) {
			localStorage.removeItem(SELECTED_CLIENT_KEY);
			return;
		}
		localStorage.setItem(SELECTED_CLIENT_KEY, JSON.stringify(value));
	} catch {
		// Quota exceeded / private-mode storage disabled — a failed persist must
		// not break the client switch (the in-memory state is the source of truth
		// for this session anyway).
	}
};

const readEpochs = (): ClientEpochMap => {
	const raw = localStorage.getItem(SELECTED_CLIENT_EPOCHS_KEY);
	if (raw === null) return {};
	try {
		const parsed = JSON.parse(raw) as unknown;
		if (parsed === null || typeof parsed !== "object") return {};
		const result: ClientEpochMap = {};
		for (const [key, value] of Object.entries(parsed)) {
			const numKey = Number(key);
			if (
				Number.isInteger(numKey) &&
				typeof value === "number" &&
				Number.isInteger(value)
			) {
				result[numKey] = value;
			}
		}
		return result;
	} catch {
		return {};
	}
};

const writeEpochs = (map: ClientEpochMap): void => {
	try {
		localStorage.setItem(SELECTED_CLIENT_EPOCHS_KEY, JSON.stringify(map));
	} catch {
		// See writeStoredSelectedClient — a failed persist must not propagate into
		// the client-switch flow and break it.
	}
};

export const getClientEpoch = (sabClientID: number): number =>
	readEpochs()[sabClientID] ?? 0;

/**
 * Increment the epoch counter for {@link sabClientID}. Returns the new value.
 * Dispatches a window event so React hooks can re-render without polling.
 */
export const bumpClientEpoch = (sabClientID: number): number => {
	const epochs = readEpochs();
	const next = (epochs[sabClientID] ?? 0) + 1;
	epochs[sabClientID] = next;
	writeEpochs(epochs);
	window.dispatchEvent(
		new CustomEvent(CLIENT_EPOCH_CHANGED_EVENT, {
			detail: { sabClientID, epoch: next },
		}),
	);
	return next;
};

export const clearClientEpochs = (): void => {
	localStorage.removeItem(SELECTED_CLIENT_EPOCHS_KEY);
	window.dispatchEvent(
		new CustomEvent(CLIENT_EPOCH_CHANGED_EVENT, { detail: null }),
	);
};

export const clientDisplayName = (c: SabClientListItemDto): string => {
	if (c.companyName) return c.companyName;
	const parts = [c.titleBefore, c.firstName, c.lastName, c.titleAfter].filter(
		(p): p is string => !!p && p.trim().length > 0,
	);
	const joined = parts.join(" ").trim();
	return joined.length > 0 ? joined : "—";
};

export const sabClientIdentificationNumber = (
	c: SabClientListItemDto,
): string =>
	c.personalIdentificationNumber ?? c.companyIdentificationNumber ?? "";
